import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../services/api";
import "../assets/css/BuscaParceria.css";

// Imóveis de outras imobiliárias no perfil do cliente, com a parceria de cada anunciante.
// Fonte: anúncios da coleta semanal dos portais — por isso cada card mostra a data da coleta.

const ORDENS = [
  { valor: "mercado", rotulo: "Melhor preço x mercado" },
  { valor: "preco", rotulo: "Menor preço" },
  { valor: "preco_desc", rotulo: "Maior preço" },
  { valor: "recente", rotulo: "Coleta mais recente" },
];
const PARCERIA = [
  { valor: "parceiros", rotulo: "Só quem faz parceria" },
  { valor: "contrato", rotulo: "Só com contrato" },
  { valor: "todos", rotulo: "Todos (inclui sem cadastro)" },
];
const INICIAL = {
  oferta: "VENDA", tipos: ["APARTAMENTO"], bairros: [], quartos_min: "", vagas_min: "",
  preco_min: "", preco_max: "", area_min: "", quadra: "", parceria: "parceiros", ordem: "mercado",
};

const moeda = (v) => (v == null ? "—" : Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 }));
const data = (iso) => (iso ? iso.split("-").reverse().join("/") : "—");
const titulo = (s) => String(s || "").toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase());
const soDigitos = (v) => String(v || "").replace(/\D/g, "");

function SeloMercado({ item }) {
  if (item.suspeito) return <span className="bp-selo bp-selo--suspeito" title="Muito abaixo do mercado do bairro: costuma ser erro no anúncio">Preço suspeito — confira</span>;
  if (item.dif_mercado == null) return <span className="bp-selo">Sem referência do bairro</span>;
  const pct = Math.round(item.dif_mercado * 100);
  if (pct <= -10) return <span className="bp-selo bp-selo--abaixo">{Math.abs(pct)}% abaixo do mercado</span>;
  if (pct >= 10) return <span className="bp-selo bp-selo--acima">{pct}% acima do mercado</span>;
  return <span className="bp-selo bp-selo--dentro">No preço de mercado</span>;
}

function SeloParceria({ p }) {
  if (!p) return <span className="bp-parc bp-parc--sem">Sem cadastro de parceria</span>;
  if (p.negativo) return <span className="bp-parc bp-parc--neg" title={p.observacao || ""}>⚠ Histórico negativo</span>;
  if (!p.faz_parceria) return <span className="bp-parc bp-parc--sem">Não faz parceria</span>;
  return (
    <span className="bp-parc bp-parc--ok">
      Parceria {p.percentual || "—"}{p.tem_contrato ? " · contrato ✓" : ""}
    </span>
  );
}

function Card({ item }) {
  const detalhes = [
    item.quartos ? `${item.quartos} quarto${item.quartos > 1 ? "s" : ""}` : null,
    item.vagas ? `${item.vagas} vaga${item.vagas > 1 ? "s" : ""}` : null,
    item.area ? `${item.area} m²` : null,
  ].filter(Boolean).join(" · ");
  return (
    <article className="bp-card">
      <div className="bp-card-topo">
        <strong className="bp-preco">{moeda(item.preco)}</strong>
        <SeloMercado item={item} />
      </div>
      <p className="bp-local">{titulo(item.tipo)} · {titulo(item.bairro)}{item.quadra ? ` · ${item.quadra}` : ""}</p>
      <p className="bp-detalhes">{detalhes || "Sem detalhes no anúncio"}</p>
      <p className="bp-m2">
        {item.valor_m2 ? `${moeda(item.valor_m2)}/m²` : "R$/m² não informado"}
        {item.m2_mercado ? ` · bairro ${moeda(item.m2_mercado)}/m²` : ""}
      </p>
      <div className="bp-anunciante">
        <span>{titulo(item.anunciante)}{item.creci ? ` · CRECI ${item.creci}` : ""}</span>
        <SeloParceria p={item.parceria} />
      </div>
      <div className="bp-rodape">
        <small>Coletado em {data(item.data_coleta)}</small>
        <div className="bp-links">
          {item.links.filter((l) => /^https?:\/\//.test(l.url)).map((l) => (
            <a key={l.url} href={l.url} target="_blank" rel="noreferrer noopener">Ver no {l.portal}</a>
          ))}
        </div>
      </div>
    </article>
  );
}

export default function BuscaParceria() {
  const [filtros, setFiltros] = useState(INICIAL);
  const [opcoes, setOpcoes] = useState(null);
  const [bairroDigitado, setBairroDigitado] = useState("");
  const [resultado, setResultado] = useState(null);
  const [pagina, setPagina] = useState(1);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    let vivo = true;
    api.get(`/parcerias/busca/opcoes?oferta=${filtros.oferta}`)
      .then((d) => { if (vivo) setOpcoes(d); })
      .catch(() => { if (vivo) setOpcoes({ bairros: [], tipos: [] }); });
    return () => { vivo = false; };
  }, [filtros.oferta]);

  const set = (campo, valor) => setFiltros((f) => ({ ...f, [campo]: valor }));
  const alternarTipo = (t) => set("tipos", filtros.tipos.includes(t) ? filtros.tipos.filter((x) => x !== t) : [...filtros.tipos, t]);
  const bairrosValidos = useMemo(() => new Set((opcoes?.bairros || []).map((b) => b.valor)), [opcoes]);
  const adicionarBairro = (valor) => {
    const b = String(valor || "").trim().toUpperCase();
    if (b && bairrosValidos.has(b) && !filtros.bairros.includes(b)) set("bairros", [...filtros.bairros, b]);
    setBairroDigitado("");
  };

  const buscar = useCallback(async (pag = 1) => {
    setCarregando(true); setErro("");
    const q = new URLSearchParams({ oferta: filtros.oferta, parceria: filtros.parceria, ordem: filtros.ordem, pagina: String(pag) });
    filtros.tipos.forEach((t) => q.append("tipo", t));
    filtros.bairros.forEach((b) => q.append("bairro", b));
    ["quartos_min", "vagas_min", "area_min"].forEach((c) => { if (filtros[c]) q.set(c, filtros[c]); });
    ["preco_min", "preco_max"].forEach((c) => { if (soDigitos(filtros[c])) q.set(c, soDigitos(filtros[c])); });
    if (filtros.quadra.trim()) q.set("quadra", filtros.quadra.trim());
    try {
      const d = await api.get(`/parcerias/busca?${q}`);
      setResultado(d); setPagina(pag);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErro(e.message || "Não foi possível buscar agora.");
    } finally {
      setCarregando(false);
    }
  }, [filtros]);

  const totalPaginas = resultado ? Math.max(1, Math.ceil(resultado.total / resultado.por_pagina)) : 1;

  return (
    <main className="bp-page">
      <header className="bp-hero">
        <small>61 IMÓVEIS · PARCERIAS</small>
        <h1>Imóveis de parceiros</h1>
        <p>
          Anúncios de outras imobiliárias no perfil do seu cliente, com o percentual de cada parceria.
          {opcoes?.coleta_ate && ` Coleta dos portais de ${data(opcoes.coleta_desde)} a ${data(opcoes.coleta_ate)}.`}
        </p>
      </header>

      <form className="bp-filtros" onSubmit={(e) => { e.preventDefault(); buscar(1); }}>
        <div className="bp-linha">
          <label>Negócio
            <select value={filtros.oferta} onChange={(e) => setFiltros({ ...filtros, oferta: e.target.value, bairros: [] })}>
              <option value="VENDA">Venda</option>
              <option value="ALUGUEL">Aluguel</option>
            </select>
          </label>
          <label>Quartos (mín.)
            <select value={filtros.quartos_min} onChange={(e) => set("quartos_min", e.target.value)}>
              <option value="">Qualquer</option>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}+</option>)}
            </select>
          </label>
          <label>Vagas (mín.)
            <select value={filtros.vagas_min} onChange={(e) => set("vagas_min", e.target.value)}>
              <option value="">Qualquer</option>
              {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}+</option>)}
            </select>
          </label>
          <label>Preço mín.
            <input inputMode="numeric" placeholder="R$" value={filtros.preco_min ? moeda(soDigitos(filtros.preco_min)) : ""}
              onChange={(e) => set("preco_min", soDigitos(e.target.value))} />
          </label>
          <label>Preço máx.
            <input inputMode="numeric" placeholder="R$" value={filtros.preco_max ? moeda(soDigitos(filtros.preco_max)) : ""}
              onChange={(e) => set("preco_max", soDigitos(e.target.value))} />
          </label>
          <label>Área mín. (m²)
            <input inputMode="numeric" value={filtros.area_min} onChange={(e) => set("area_min", soDigitos(e.target.value))} />
          </label>
        </div>

        <div className="bp-bloco">
          <span className="bp-rotulo">Tipo</span>
          <div className="bp-chips">
            {(opcoes?.tipos || []).slice(0, 10).map((t) => (
              <button type="button" key={t.valor} aria-pressed={filtros.tipos.includes(t.valor)}
                className={`bp-chip ${filtros.tipos.includes(t.valor) ? "is-on" : ""}`} onClick={() => alternarTipo(t.valor)}>
                {titulo(t.valor)}
              </button>
            ))}
          </div>
        </div>

        <div className="bp-bloco">
          <label className="bp-rotulo" htmlFor="bp-bairro">Bairros <em>(vazio = todos)</em></label>
          <div className="bp-chips">
            {filtros.bairros.map((b) => (
              <button type="button" key={b} className="bp-chip is-on" title="Remover"
                onClick={() => set("bairros", filtros.bairros.filter((x) => x !== b))}>{titulo(b)} ✕</button>
            ))}
            <input id="bp-bairro" list="bp-bairros" className="bp-bairro-input" placeholder="Digite e escolha um bairro"
              value={bairroDigitado} onChange={(e) => {
                const v = e.target.value;
                if (bairrosValidos.has(v.trim().toUpperCase())) adicionarBairro(v); else setBairroDigitado(v);
              }}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); adicionarBairro(bairroDigitado); } }} />
            <datalist id="bp-bairros">
              {(opcoes?.bairros || []).map((b) => <option key={b.valor} value={b.valor}>{b.total} anúncios</option>)}
            </datalist>
          </div>
        </div>

        <div className="bp-linha">
          <label>Quadra / endereço contém
            <input value={filtros.quadra} placeholder="Ex.: SQSW 300, QI 23" onChange={(e) => set("quadra", e.target.value)} />
          </label>
          <label>Parceria
            <select value={filtros.parceria} onChange={(e) => set("parceria", e.target.value)}>
              {PARCERIA.map((p) => <option key={p.valor} value={p.valor}>{p.rotulo}</option>)}
            </select>
          </label>
          <label>Ordenar por
            <select value={filtros.ordem} onChange={(e) => set("ordem", e.target.value)}>
              {ORDENS.map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
            </select>
          </label>
          <div className="bp-acoes">
            <button type="button" className="bp-btn bp-btn--sec" onClick={() => { setFiltros(INICIAL); setResultado(null); }}>Limpar</button>
            <button type="submit" className="bp-btn" disabled={carregando}>{carregando ? "Buscando…" : "Buscar imóveis"}</button>
          </div>
        </div>
      </form>

      {erro && <div className="bp-erro" role="alert">{erro}</div>}

      {resultado && (
        <section aria-live="polite">
          <div className="bp-resumo">
            <strong>{resultado.total.toLocaleString("pt-BR")} imóvel(is)</strong>
            {resultado.por_parceiro?.length > 0 && (
              <span className="bp-resumo-parc">
                {resultado.por_parceiro.slice(0, 6).map((p) => `${p.nome} ${p.total}`).join(" · ")}
              </span>
            )}
          </div>
          {resultado.truncado && (
            <p className="bp-aviso">Busca muito ampla: a lista pode estar incompleta. Escolha bairro ou faixa de preço.</p>
          )}
          {resultado.total === 0 ? (
            <p className="bp-vazio">Nenhum imóvel de parceiro nesse perfil. Tente ampliar bairros, preço ou "Todos" em Parceria.</p>
          ) : (
            <div className="bp-grade">
              {resultado.itens.map((i) => <Card key={i.links[0]?.url || `${i.anunciante}-${i.preco}`} item={i} />)}
            </div>
          )}
          {totalPaginas > 1 && (
            <div className="bp-paginacao">
              <button type="button" className="bp-btn bp-btn--sec" disabled={pagina <= 1 || carregando} onClick={() => buscar(pagina - 1)}>Anterior</button>
              <span>Página {pagina} de {totalPaginas}</span>
              <button type="button" className="bp-btn bp-btn--sec" disabled={pagina >= totalPaginas || carregando} onClick={() => buscar(pagina + 1)}>Próxima</button>
            </div>
          )}
          <p className="bp-nota">
            Dados da coleta semanal dos portais: o anúncio pode ter saído do ar ou mudado de preço. Confirme com a
            imobiliária antes de apresentar ao cliente. "Mercado" é a mediana do R$/m² do bairro no estudo da 61.
          </p>
        </section>
      )}
    </main>
  );
}
